import { Add, Close } from '@mui/icons-material';
import { Box, Button, Drawer, IconButton, InputLabel, TextField, Typography, CircularProgress } from '@mui/material';
import ResourceAPI from 'api/ResourceAPI';
import { Loader } from 'components';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import Customer from 'types/entities/Customer';
import User from 'types/entities/User';
import Tenant from 'types/entities/Tenant';
import { ActionDescriptor } from 'types/enums/ActionDescriptor';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import useUserRoles from 'hooks/useUserRoles';

type FormData = {
  customer_id: number;
  department: string;
  direction: string;
  first_name: string;
  last_name: string;
  mail: string;
  phone: string;
  position: string;
  have_cognito_account: boolean;
  tenants: Tenant[];
};

type Props = {
  activeCustomer: Customer;
  open: boolean;
  dispatchOpen: () => void;
  setClient: (user: User) => void;
  adminTenants: Tenant[];
};

const ClientCreation: React.FC<Props> = ({ activeCustomer, open, dispatchOpen, setClient, adminTenants }: Props) => {
  const { dispatch } = useAppState();
  const appState = useSelector((state) => state.app);
  const [, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const userRoles = useUserRoles();

  // Form data and methods
  const [clientToCreate] = useState({
    customer_id: appState.customer?.id || activeCustomer.id,
    department: '',
    direction: '',
    first_name: '',
    is_archived: false,
    is_client: true,
    last_name: '',
    mail: '',
    phone: '',
    position: '',
    have_cognito_account: false,
    tenants: userRoles.isAdmin
      ? adminTenants.filter((tenant) => tenant.id === (appState.customer?.id || activeCustomer.id))
      : [],
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: clientToCreate,
  });

  const [t] = useTranslation();

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);

    try {
      if (activeCustomer) {
        const { have_cognito_account, ...clientToSend } = data;

        const transformedData = {
          ...clientToSend,
          action_id: ActionDescriptor.CreateClient,
          tenants: (data.tenants || []).map((tenant) => ({
            id: tenant.id,
            slug: tenant.slug,
            name: tenant.name,
          })),
        };

        const {
          data: { user_client },
        } = await ResourceAPI.post('users/clients', transformedData);

        setSubmitted(true);
        setClient(user_client);
        dispatchOpen();
      }
    } catch (e: any) {
      const result = (e as Error).message;
      if (result) {
        if (e.response && e.response.status === 422) {
          dispatch(
            setSnackbarAction({
              message: `${e.response.data.message}`,
              open: true,
              severity: 'error',
            })
          );
        } else dispatch(setSnackbarAction({ message: `Error with client: ${result}`, open: true, severity: 'error' }));
      } else dispatch(setSnackbarAction({ message: 'Error with client', open: true, severity: 'error' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={dispatchOpen}
      PaperProps={{
        sx: {
          width: {
            xs: '100%',
            lg: 450,
          },
        },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 5 }}>
          <Typography variant="h2">Create client</Typography>
          <IconButton sx={{ ml: 'auto' }} color="primary" onClick={dispatchOpen} disabled={isLoading}>
            <Close />
          </IconButton>
        </Box>

        <Box>
          <Controller
            name="first_name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_first_name" error={errors.first_name && true}>
                  First name{' '}
                  <Typography display="inline" color="error">
                    *
                  </Typography>
                </InputLabel>
                <TextField
                  id="client_first_name"
                  onChange={onChange}
                  value={value}
                  fullWidth
                  disabled={isLoading}
                  helperText={errors.first_name?.message}
                  error={errors.first_name && true}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
            )}
            rules={{
              required: {
                value: true,
                message: 'Your must enter a first name',
              },
              pattern: {
                value: /^[a-zA-ZÀ-ÿ\s'-]+$/,
                message: 'Special characters and numbers are not allowed',
              },
            }}
          />

          <Controller
            name="last_name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_last_name" error={errors.last_name && true}>
                  Last name{' '}
                  <Typography display="inline" color="error">
                    *
                  </Typography>
                </InputLabel>
                <TextField
                  id="client_last_name"
                  onChange={onChange}
                  value={value}
                  fullWidth
                  disabled={isLoading}
                  helperText={errors.last_name?.message}
                  error={errors.last_name && true}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
            )}
            rules={{
              required: {
                value: true,
                message: 'Your must enter a last name',
              },
              pattern: {
                value: /^[a-zA-ZÀ-ÿ\s'-]+$/,
                message: 'Special characters and numbers are not allowed',
              },
            }}
          />

          <Controller
            name="position"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_position" error={errors.position && true}>
                  Position{' '}
                </InputLabel>
                <TextField
                  id="client_position"
                  onChange={onChange}
                  value={value}
                  fullWidth
                  disabled={isLoading}
                  helperText={errors.position?.message}
                  error={errors.position && true}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
            )}
            rules={{
              pattern: {
                value: /^[a-zA-Z0-9\s\-'(),.&]+$/i,
                message: 'Special characters are not allowed',
              },
            }}
          />

          <Controller
            name="mail"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_mail" error={errors.mail && true}>
                  Mail{' '}
                  <Typography display="inline" color="error">
                    *
                  </Typography>
                </InputLabel>
                <TextField
                  id="client_mail"
                  onChange={(e) => onChange(e.target.value.toLowerCase())}
                  value={value}
                  fullWidth
                  disabled={isLoading}
                  helperText={errors.mail?.message}
                  error={errors.mail && true}
                  inputProps={{ maxLength: 254 }}
                />
              </Box>
            )}
            rules={{
              required: {
                value: true,
                message: 'Your must enter an email',
              },
              pattern: {
                value:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i, // eslint-disable-line max-len
                message: 'Please enter a valid email',
              },
            }}
          />

          <Controller
            name="department"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_department" error={errors.department && true}>
                  Department{' '}
                </InputLabel>
                <TextField
                  id="client_department"
                  onChange={onChange}
                  value={value}
                  fullWidth
                  disabled={isLoading}
                  helperText={errors.department?.message}
                  error={errors.department && true}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
            )}
            rules={{
              pattern: {
                value: /^[a-zA-Z0-9\s\-'().&]+$/i,
                message: 'Special characters are not allowed',
              },
            }}
          />

          <Controller
            name="direction"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_direction" error={errors.direction && true}>
                  Direction{' '}
                </InputLabel>
                <TextField
                  id="client_direction"
                  onChange={onChange}
                  value={value}
                  fullWidth
                  disabled={isLoading}
                  helperText={errors.direction?.message}
                  error={errors.direction && true}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
            )}
            rules={{
              pattern: {
                value: /^[a-zA-Z0-9\s\-'().&]+$/i,
                message: 'Special characters are not allowed',
              },
            }}
          />
          {userRoles.isAdmin && (
            <Controller
              name="tenants"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Box sx={{ mb: 3 }}>
                  <InputLabel>Select Tenants</InputLabel>
                  <Autocomplete
                    multiple
                    options={adminTenants}
                    value={value || []}
                    onChange={(_, newValue) => onChange(newValue)}
                    getOptionLabel={(option: Tenant) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disableCloseOnSelect
                    disableClearable
                    disabled={isLoading}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option.name}
                      </li>
                    )}
                    renderTags={() => null}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select tenants" fullWidth disabled={isLoading} />
                    )}
                  />
                </Box>
              )}
            />
          )}

          <Button
            data-testid="client_creation_create"
            startIcon={isLoading ? null : <Add />}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="success"
            fullWidth
            disabled={isLoading}
            sx={{ minHeight: 56 }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>Creating...</span>
              </Box>
            ) : (
              t('Create_client')
            )}
          </Button>
        </Box>
      </form>
    </Drawer>
  );
};

export default ClientCreation;
